import React from 'react'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts'
import FacilityLoader from './FacilityLoader'

function formatCurrency(value) {
	if (!value) return '$0'
	if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`
	return `$${value.toFixed(0)}`
}


function formatHour(v) {
	if (!v || !v.includes('~')) return v
	const h = parseInt(v.split('~')[0])
	if (h === 0) return '12 AM'
	if (h === 12) return '12 PM'
	return h < 12 ? `${h} AM` : `${h - 12} PM`
}

function formatFacilityLabel(code, city, state) {
	if (city && state) return `${code} (${city}, ${state})`
	return code
}

function formatRatePerMile(revenue, distance) {
	if (!distance || distance <= 0) return '$0.00'
	return `$${((revenue || 0) / distance).toFixed(2)}`
}

export default function LoadActivityTab({ data, period, onPeriodChange, loading, error, isDarkMode }) {
	if (loading) {
		return <FacilityLoader isDarkMode={isDarkMode} label="Loading load activity" />
	}

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-8 text-gray-400">
				<svg className="w-10 h-10 mb-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
				</svg>
				<p className="text-sm text-red-400">{error}</p>
			</div>
		)
	}

	return (
		<div className="space-y-2">
			{/* Time Toggle */}
			<div className="flex justify-end">
				<div className={`flex rounded-lg overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
					{['24h', '7d'].map(p => (
						<button
							key={p}
							onClick={() => onPeriodChange(p)}
							className={`px-2 py-0.5 text-xs font-medium ${
								period === p
									? 'bg-blue-500 text-white'
									: isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
							}`}
						>
							{p}
						</button>
					))}
				</div>
			</div>

			{!data && (
				<p className="text-center text-gray-500 py-4">No load activity data available</p>
			)}

			{/* Peak Activity Hours */}
			{data?.peakHours && (
				<div className={`rounded-lg p-3 ${
					isDarkMode
						? 'bg-gradient-to-r from-blue-900/30 to-emerald-900/30 border border-blue-800/30'
						: 'bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-200/50'
				}`}>
					<div className="flex items-center gap-3">
						<div className={`p-1.5 rounded ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
							<svg className="w-3 h-3 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						</div>
						<p className="text-xs">
							<span className="font-medium">Peak Activity Hours:</span>{' '}
							<span className="text-gray-500">{data.peakHours}</span>
						</p>
					</div>
				</div>
			)}

			{/* Activity Trends */}
			{data?.trend?.daily?.length > 0 && (
				<div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
					<div className="flex items-center justify-between mb-3">
						<h4 className="text-sm font-medium">Activity Trends</h4>
						<div className="flex items-center gap-4 text-xs">
							<span className="flex items-center gap-1.5">
								<span className="w-2 h-2 rounded-full bg-blue-500" />
								<span className="text-gray-500">Pickups</span>
							</span>
							<span className="flex items-center gap-1.5">
								<span className="w-2 h-2 rounded-full bg-emerald-500" />
								<span className="text-gray-500">Drop-offs</span>
							</span>
						</div>
					</div>
					<ResponsiveContainer width="100%" height={140}>
						<AreaChart data={data.trend.daily} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
							<defs>
								<linearGradient id="pickupGrad" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
									<stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
								</linearGradient>
								<linearGradient id="dropoffGrad" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
									<stop offset="95%" stopColor="#10b981" stopOpacity={0} />
								</linearGradient>
							</defs>
							<XAxis
								dataKey="date"
								tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
								tickLine={false}
								axisLine={false}
								interval={period === '24h' ? 1 : 0}
								tickFormatter={formatHour}
							/>
							<YAxis
								tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 10 }}
								tickLine={false}
								axisLine={false}
								allowDecimals={false}
							/>
							<Tooltip
								contentStyle={{
									backgroundColor: isDarkMode ? '#1f2937' : '#fff',
									border: 'none',
									borderRadius: '8px',
									fontSize: '12px',
									boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
									color: isDarkMode ? '#e5e7eb' : '#111827',
								}}
							/>
							<Area type="monotone" dataKey="pickups" name="Pickups" stroke="#3b82f6" strokeWidth={2} fill="url(#pickupGrad)" />
							<Area type="monotone" dataKey="dropoffs" name="Drop-offs" stroke="#10b981" strokeWidth={2} fill="url(#dropoffGrad)" />
						</AreaChart>
					</ResponsiveContainer>
				</div>
			)}

			{/* Popular Lanes - Separated into Outbound and Inbound */}
			{data?.popularLanes?.length > 0 && (
				<div className="space-y-3">
					{data.popularLanes.filter(l => l.type === 'pickup').length > 0 && (
						<div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
							<div className="flex items-center justify-between mb-3">
								<div className="flex items-center gap-2">
									<div className={`p-1.5 rounded ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
										<svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M17 7H7M17 7V17" />
										</svg>
									</div>
									<div>
										<h4 className="text-sm font-medium">Outbound Loads</h4>
										<p className="text-[10px] text-gray-500">Picked up from {data.facility}, delivered to:</p>
									</div>
								</div>
								<div className="flex items-center gap-2 text-xs text-gray-500">
									<span>{data.outbound?.count || 0} loads</span>
									<span>|</span>
									<span>{formatCurrency(data.outbound?.revenue)}</span>
									<span>|</span>
									<span>{formatRatePerMile(data.outbound?.revenue, data.outbound?.distance)}/mi</span>
								</div>
							</div>
							<div className="space-y-1">
								{data.popularLanes.filter(l => l.type === 'pickup').slice(0, 5).map((lane, i) => (
									<div key={i} className={`flex items-center justify-between p-2 rounded text-sm ${
										isDarkMode ? 'bg-gray-800/50' : 'bg-white'
									}`}>
										<div className="flex items-center gap-2 min-w-0">
											<span className="font-medium text-xs">{formatFacilityLabel(lane.facility, lane.city, lane.state)}</span>
										</div>
										<div className="flex items-center gap-3 text-xs flex-shrink-0">
											<span className="text-gray-500">{lane.count} loads</span>
											<span className="font-medium">{formatCurrency(lane.revenue)}</span>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{data.popularLanes.filter(l => l.type === 'dropoff').length > 0 && (
						<div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
							<div className="flex items-center justify-between mb-3">
								<div className="flex items-center gap-2">
									<div className={`p-1.5 rounded ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
										<svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M17 7L7 17M7 17H17M7 17V7" />
										</svg>
									</div>
									<div>
										<h4 className="text-sm font-medium">Inbound Loads</h4>
										<p className="text-[10px] text-gray-500">Delivered to {data.facility}, picked up from:</p>
									</div>
								</div>
								<div className="flex items-center gap-2 text-xs text-gray-500">
									<span>{data.inbound?.count || 0} loads</span>
									<span>|</span>
									<span>{formatCurrency(data.inbound?.revenue)}</span>
									<span>|</span>
									<span>{formatRatePerMile(data.inbound?.revenue, data.inbound?.distance)}/mi</span>
								</div>
							</div>
							<div className="space-y-1">
								{data.popularLanes.filter(l => l.type === 'dropoff').slice(0, 5).map((lane, i) => (
									<div key={i} className={`flex items-center justify-between p-2 rounded text-sm ${
										isDarkMode ? 'bg-gray-800/50' : 'bg-white'
									}`}>
										<div className="flex items-center gap-2 min-w-0">
											<span className="font-medium text-xs">{formatFacilityLabel(lane.facility, lane.city, lane.state)}</span>
										</div>
										<div className="flex items-center gap-3 text-xs flex-shrink-0">
											<span className="text-gray-500">{lane.count} loads</span>
											<span className="font-medium">{formatCurrency(lane.revenue)}</span>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}

		</div>
	)
}
